import React from "react";
import { extractPreviewPartyExpenseLines, formatInr } from "../lib/accountsSettlement";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const PCT_PRESETS = [1, 1.5, 2, 2.5, 3];

function normalizeBasis(basis) {
  const b = String(basis || "GROSS_AMOUNT_PCT").toUpperCase();
  if (b === "FIXED_PER_ORDER") return "FIXED_PER_VEHICLE";
  if (b === "FIXED_PER_QTL" || b === "GROSS_AMOUNT_PCT") return b;
  return "GROSS_AMOUNT_PCT";
}

function computeAmount(ln, { gross, billableQtl }) {
  const basis = normalizeBasis(ln.basis);
  const rate = Number(ln.rate || 0);
  if (!(rate > 0)) return Number(ln.amount || 0);
  if (basis === "FIXED_PER_QTL") {
    return Math.round(billableQtl * rate * 100) / 100;
  }
  if (gross > 0) {
    return Math.round(gross * (rate / 100) * 100) / 100;
  }
  return Number(ln.amount || 0);
}

function rateSelectValue(basis, rate) {
  if (normalizeBasis(basis) === "FIXED_PER_QTL") return "per_qtl";
  const n = Number(rate);
  if (PCT_PRESETS.some((p) => Math.abs(p - n) < 0.001)) return String(n);
  if (rate != null && rate !== "" && Number(rate) > 0) return "custom";
  return "custom";
}

export default function PartyExpenseEditor({
  preview,
  form,
  onChange,
  onReset,
  frozen = false,
  source = "live",
  mode = "voucher",
  billableQtl = 0,
  grossAmount = 0,
}) {
  const fromForm = Boolean(
    form?.party_expense_manual
    || (frozen && (form?.party_expense_lines?.length || 0) > 0),
  );
  const lines = fromForm
    ? (form?.party_expense_lines || [])
    : extractPreviewPartyExpenseLines(preview);

  if (!lines.length && !form?.party_expense_manual) {
    return null;
  }

  const gross = Number(grossAmount || preview?.gross_amount || preview?.gross || 0);
  const qtl = Number(billableQtl || preview?.billable_qtl || preview?.net_weight_qtl || 0);

  const updateLine = (index, patch) => {
    const base = form?.party_expense_lines || lines;
    const next = base.map((ln, i) => {
      if (i !== index) return ln;
      const merged = { ...ln, ...patch };
      if (merged.basis != null || merged.rate != null) {
        merged.basis = normalizeBasis(merged.basis ?? ln.basis);
        merged.amount = computeAmount(merged, { gross, billableQtl: qtl });
      }
      return merged;
    });
    onChange({ party_expense_lines: next, party_expense_manual: true });
  };

  const setLineRateMode = (index, modeValue) => {
    const ln = (form?.party_expense_lines || lines)[index] || {};
    if (modeValue === "per_qtl") {
      updateLine(index, {
        basis: "FIXED_PER_QTL",
        rate: normalizeBasis(ln.basis) === "FIXED_PER_QTL" && ln.rate != null ? ln.rate : "",
      });
      return;
    }
    if (modeValue === "custom") {
      updateLine(index, {
        basis: "GROSS_AMOUNT_PCT",
        rate: PCT_PRESETS.some((p) => Math.abs(p - Number(ln.rate)) < 0.001) ? "" : (ln.rate ?? ""),
      });
      return;
    }
    updateLine(index, { basis: "GROSS_AMOUNT_PCT", rate: Number(modeValue) });
  };

  const displayLines = fromForm ? (form?.party_expense_lines || lines) : lines;
  const canEdit = fromForm && !frozen;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="party-expense-editor">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-slate-900">Party expense lines</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {frozen
              ? "Frozen on save — amounts will not change when the party chart is updated."
              : source === "manual"
                ? "Manual overrides — save voucher to apply."
                : mode === "purchase"
                  ? "From party expense chart — updates automatically until purchase is finalized."
                  : "From party expense chart — updates automatically until you save this voucher."}
          </p>
        </div>
        {!frozen && form?.party_expense_manual && (
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={onReset}>
            Reset from party chart
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {displayLines.map((ln, index) => {
          const basis = normalizeBasis(ln.basis);
          const modeValue = rateSelectValue(ln.basis, ln.rate);
          const showRateUi = canEdit && (ln.basis != null || ln.rate != null);
          const amount = showRateUi
            ? computeAmount(ln, { gross, billableQtl: qtl })
            : Number(ln.amount || 0);

          return (
            <div key={`${ln.code}-${index}`} className="space-y-2">
              <Label className="text-[10px] uppercase text-muted-foreground">{ln.label || ln.code}</Label>
              {showRateUi ? (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2 items-end">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Select value={modeValue} onValueChange={(v) => setLineRateMode(index, v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {PCT_PRESETS.map((p) => (
                          <SelectItem key={p} value={String(p)}>
                            {p}% of gross
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Custom %</SelectItem>
                        <SelectItem value="per_qtl">₹ per quintal</SelectItem>
                      </SelectContent>
                    </Select>
                    {basis === "FIXED_PER_QTL" || modeValue === "custom" ? (
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="h-8 text-xs"
                        value={ln.rate ?? ""}
                        placeholder={basis === "FIXED_PER_QTL" ? "₹ / qtl" : "%"}
                        onChange={(e) => updateLine(index, {
                          rate: e.target.value,
                          basis: basis === "FIXED_PER_QTL" ? "FIXED_PER_QTL" : "GROSS_AMOUNT_PCT",
                        })}
                      />
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground tabular pb-1 sm:text-right">
                    ≈ {formatInr(amount)}
                    <span className="block text-[10px]">
                      {ln.capitalized_into_inventory
                        ? "Capitalized into inventory"
                        : ln.effect === "add"
                          ? "Added to bill"
                          : "Deducted"}
                    </span>
                  </p>
                </div>
              ) : canEdit ? (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2 items-end">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-8 text-xs"
                    value={ln.amount ?? ""}
                    onChange={(e) => updateLine(index, { amount: e.target.value })}
                  />
                  <p className="text-[10px] text-muted-foreground pb-1 sm:text-right">
                    {ln.capitalized_into_inventory
                      ? "Capitalized into inventory"
                      : ln.effect === "add"
                        ? "Added to bill"
                        : "Deducted"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2 items-end">
                  <p className="text-sm tabular mt-1">{formatInr(ln.amount || 0)}</p>
                  <p className="text-[10px] text-muted-foreground pb-1 sm:text-right">
                    {ln.capitalized_into_inventory
                      ? "Capitalized into inventory"
                      : ln.effect === "add"
                        ? "Added to bill"
                        : "Deducted"}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
